import * as React from "react";
import { Form, Formik, FormikProps } from "formik";

import { Box, Button, Card, Grid, Stack, TextField, Typography, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { SelectOption } from "@mui/base";
import { ClientContactsReportFilters } from "./ClientContactsReportTable";

interface Props {
  initialFilters?: ClientContactsReportFilters;
  onFiltersChange: (filters: ClientContactsReportFilters) => void;
  reportData?: any[]; // Used to generate dynamic filter options
}

enum FieldNames {
  firstName = "firstName",
  lastName = "lastName",
  email = "email",
  company = "company",
  branch = "branch",
  xtrfStatus = "xtrfStatus",
  cognitoStatus = "cognitoStatus",
  orgAdminStatus = "orgAdminStatus",
  cognitoXtrfConflicts = "cognitoXtrfConflicts"
}

interface FormValues {
  [FieldNames.firstName]: string;
  [FieldNames.lastName]: string;
  [FieldNames.email]: string;
  [FieldNames.company]: string;
  [FieldNames.branch]: string;
  [FieldNames.xtrfStatus]: string;
  [FieldNames.cognitoStatus]: string;
  [FieldNames.orgAdminStatus]: string;
  [FieldNames.cognitoXtrfConflicts]: string;
}

const ClientContactsReportFilter: React.FC<Props> = ({ initialFilters, onFiltersChange, reportData }) => {
  const formikRef = React.useRef<FormikProps<FormValues>>(null);

  // Current text filter values (updated immediately)
  const [currentTextFilters, setCurrentTextFilters] = React.useState({
    firstName: initialFilters?.firstName || "",
    lastName: initialFilters?.lastName || "",
    email: initialFilters?.email || "",
    company: initialFilters?.company || "",
    branch: initialFilters?.branch || ""
  });

  // Debounced state for text inputs
  const [debouncedTextFilters, setDebouncedTextFilters] = React.useState({
    firstName: initialFilters?.firstName || "",
    lastName: initialFilters?.lastName || "",
    email: initialFilters?.email || "",
    company: initialFilters?.company || "",
    branch: initialFilters?.branch || ""
  });

  // Debounce effect for text inputs (300ms delay)
  React.useEffect(() => {
    // If any text field is cleared, trigger immediately for better UX
    const hasEmptyField = Object.values(currentTextFilters).some(value => value === "");
    const previouslyHadValues = Object.values(debouncedTextFilters).some(value => value !== "");

    if (hasEmptyField && previouslyHadValues) {
      setDebouncedTextFilters(currentTextFilters);
      return;
    }

    // Otherwise, debounce with 300ms delay
    const handler = setTimeout(() => {
      setDebouncedTextFilters(currentTextFilters);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [currentTextFilters, debouncedTextFilters]);

  // Handle text input changes
  const handleTextInputChange = React.useCallback((field: keyof typeof currentTextFilters, value: string) => {
    setCurrentTextFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Also update Formik value
    formikRef.current?.setFieldValue(field, value);
  }, []);

  // Generate dynamic filter options from report data
  const xtrfStatusOptions: SelectOption<string>[] = React.useMemo(() => {
    const statuses = new Set<string>();
    reportData?.forEach(row => {
      if (row.xtrfStatus) {
        statuses.add(row.xtrfStatus);
      }
    });
    return [
      { label: "All", value: "ALL" },
      ...Array.from(statuses)
        .sort()
        .map(status => ({ label: status, value: status }))
    ];
  }, [reportData]);

  const cognitoStatusOptions: SelectOption<string>[] = React.useMemo(() => {
    const statuses = new Set<string>();
    reportData?.forEach(row => {
      if (row.cognitoStatus) {
        statuses.add(row.cognitoStatus);
      }
    });
    return [
      { label: "All", value: "ALL" },
      ...Array.from(statuses)
        .sort()
        .map(status => ({ label: status, value: status }))
    ];
  }, [reportData]);

  const orgAdminStatusOptions: SelectOption<string>[] = [
    { label: "All", value: "ALL" },
    { label: "Yes", value: "YES" },
    { label: "No", value: "NO" }
  ];

  const cognitoXtrfConflictsOptions: SelectOption<string>[] = [
    { label: "All", value: "ALL" },
    { label: "Yes", value: "YES" },
    { label: "No", value: "NO" }
  ];

  const initialValues: FormValues = React.useMemo(
    () => ({
      firstName: initialFilters?.firstName || "",
      lastName: initialFilters?.lastName || "",
      email: initialFilters?.email || "",
      company: initialFilters?.company || "",
      branch: initialFilters?.branch || "",
      xtrfStatus: initialFilters?.xtrfStatus || "ALL",
      cognitoStatus: initialFilters?.cognitoStatus || "ALL",
      orgAdminStatus: initialFilters?.orgAdminStatus || "ALL",
      cognitoXtrfConflicts: initialFilters?.cognitoXtrfConflicts || "ALL"
    }),
    [initialFilters]
  );

  // State to track dropdown filter values
  const [dropdownFilters, setDropdownFilters] = React.useState({
    xtrfStatus: initialFilters?.xtrfStatus || "ALL",
    cognitoStatus: initialFilters?.cognitoStatus || "ALL",
    orgAdminStatus: initialFilters?.orgAdminStatus || "ALL",
    cognitoXtrfConflicts: initialFilters?.cognitoXtrfConflicts || "ALL"
  });

  // Apply filters whenever debounced text filters or dropdown filters change
  React.useEffect(() => {
    const filters: ClientContactsReportFilters = {
      firstName: debouncedTextFilters.firstName || undefined,
      lastName: debouncedTextFilters.lastName || undefined,
      email: debouncedTextFilters.email || undefined,
      company: debouncedTextFilters.company || undefined,
      branch: debouncedTextFilters.branch || undefined,
      xtrfStatus: dropdownFilters.xtrfStatus !== "ALL" ? dropdownFilters.xtrfStatus : undefined,
      cognitoStatus: dropdownFilters.cognitoStatus !== "ALL" ? dropdownFilters.cognitoStatus : undefined,
      orgAdminStatus: dropdownFilters.orgAdminStatus !== "ALL" ? dropdownFilters.orgAdminStatus : undefined,
      cognitoXtrfConflicts:
        dropdownFilters.cognitoXtrfConflicts !== "ALL" ? dropdownFilters.cognitoXtrfConflicts : undefined
    };

    onFiltersChange(filters);
  }, [debouncedTextFilters, dropdownFilters, onFiltersChange]);

  const onFormFieldObserverChange = React.useCallback(() => {
    // Update dropdown filters state when Formik values change
    const currentValues = formikRef.current?.values;
    if (currentValues) {
      setDropdownFilters({
        xtrfStatus: currentValues.xtrfStatus,
        cognitoStatus: currentValues.cognitoStatus,
        orgAdminStatus: currentValues.orgAdminStatus,
        cognitoXtrfConflicts: currentValues.cognitoXtrfConflicts
      });
    }
  }, []);

  const onSubmit = React.useCallback(() => {
    // Form submission is handled by the useEffect above
  }, []);

  const clearAllFilters = React.useCallback(() => {
    formikRef.current?.resetForm({
      values: {
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        branch: "",
        xtrfStatus: "ALL",
        cognitoStatus: "ALL",
        orgAdminStatus: "ALL",
        cognitoXtrfConflicts: "ALL"
      }
    });
    setCurrentTextFilters({
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      branch: ""
    });
    setDebouncedTextFilters({
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      branch: ""
    });
    setDropdownFilters({
      xtrfStatus: "ALL",
      cognitoStatus: "ALL",
      orgAdminStatus: "ALL",
      cognitoXtrfConflicts: "ALL"
    });
  }, []);

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Formik initialValues={initialValues} onSubmit={onSubmit} innerRef={formikRef} enableReinitialize={true}>
        <Form noValidate={true} autoComplete="off" autoCorrect="off">
          <FormFieldObserver fieldName={FieldNames.xtrfStatus} onChange={onFormFieldObserverChange} />
          <FormFieldObserver fieldName={FieldNames.cognitoStatus} onChange={onFormFieldObserverChange} />
          <FormFieldObserver fieldName={FieldNames.orgAdminStatus} onChange={onFormFieldObserverChange} />
          <FormFieldObserver fieldName={FieldNames.cognitoXtrfConflicts} onChange={onFormFieldObserverChange} />

          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Filter Contacts</Typography>
              <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </Box>

            {/* Text-based filters */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  name={FieldNames.firstName}
                  label="First Name"
                  placeholder="Search first name..."
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={currentTextFilters.firstName}
                  onChange={e => handleTextInputChange("firstName", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  name={FieldNames.lastName}
                  label="Last Name"
                  placeholder="Search last name..."
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={currentTextFilters.lastName}
                  onChange={e => handleTextInputChange("lastName", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  name={FieldNames.email}
                  label="Email"
                  placeholder="Search email..."
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={currentTextFilters.email}
                  onChange={e => handleTextInputChange("email", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  name={FieldNames.company}
                  label="Company"
                  placeholder="Search company..."
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={currentTextFilters.company}
                  onChange={e => handleTextInputChange("company", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  name={FieldNames.branch}
                  label="Branch"
                  placeholder="Search branch..."
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={currentTextFilters.branch}
                  onChange={e => handleTextInputChange("branch", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            {/* Dropdown filters */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectOneField
                  name={FieldNames.xtrfStatus}
                  label="Active in XTRF"
                  options={xtrfStatusOptions}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectOneField
                  name={FieldNames.cognitoStatus}
                  label="Cognito Status"
                  options={cognitoStatusOptions}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectOneField
                  name={FieldNames.orgAdminStatus}
                  label="Org Admin"
                  options={orgAdminStatusOptions}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectOneField
                  name={FieldNames.cognitoXtrfConflicts}
                  label="Cognito/XTRF Conflicts"
                  options={cognitoXtrfConflictsOptions}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Stack>
        </Form>
      </Formik>
    </Card>
  );
};

export { ClientContactsReportFilter };
